<template>
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      @click.self="emit('close')"
    >
      <div
        class="relative w-full max-w-md rounded-[20px] border border-tertiary bg-secondary p-8 shadow-2xl"
      >
        <h2 class="mb-6 border-b border-tertiary pb-4 text-xl font-bold text-text-main">
          {{ entityLabel }} umbenennen
        </h2>
        <div class="mb-6">
          <label class="block text-sm font-bold text-text-main mb-2">Neuer Name für {{ baseName }}:</label>
          <div
            class="flex items-center rounded-standard border border-tertiary bg-primary px-3 py-2 focus-within:border-accent"
          >
            <input
              v-model="inputName"
              type="text"
              class="flex-1 border-none bg-transparent text-sm text-text-main outline-none"
              @keydown.enter="submit"
            >
            <span
              v-if="isJavaFile"
              class="font-mono text-sm text-text-main/50"
            >.java</span>
          </div>
        </div>

        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            class="cursor-pointer rounded-standard bg-accent px-6 py-2 text-sm font-semibold text-text-main shadow-sm transition-all duration-150 hover:brightness-110"
            @click="submit"
          >
            Speichern
          </button>

          <button
            type="button"
            class="cursor-pointer rounded-standard border border-tertiary bg-primary px-6 py-2 text-sm font-semibold text-text-main transition-colors duration-150 hover:bg-primary/80"
            @click="emit('close')"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    file: string
    entityLabel?: string
  }>(),
  { entityLabel: 'Datei' }
)

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'save', newName: string): void
}>()

const inputName = ref('')

// Rename keeps the file in its folder, so only the basename is edited — pre-fill
// and compare against the basename, never the full path (which carries the
// folder prefix and would otherwise get doubled up).
const baseName = computed(() =>
  props.file.includes('/') ? props.file.slice(props.file.lastIndexOf('/') + 1) : props.file
)

// The .java extension is fixed and shown as a static suffix (like NewFileOverlay);
// only the stem is editable. Folder renames have no such extension.
const isJavaFile = computed(() => baseName.value.toLowerCase().endsWith('.java'))
const stem = computed(() =>
  isJavaFile.value ? baseName.value.slice(0, -'.java'.length) : baseName.value
)

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      inputName.value = stem.value
    }
  }
)

function submit() {
  const trimmed = inputName.value.trim()
  if (!trimmed) {
    emit('close')
    return
  }
  const newName = isJavaFile.value ? `${trimmed}.java` : trimmed
  if (newName !== baseName.value) {
    emit('save', newName)
  }
  emit('close')
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
